"use strict";
/**
 * App description (data-model APP-DESCRIPTION.md): how an HDS app names the app holding an access.
 *
 * Three sources, one rule: the operator's catalogue (`vouched`), an OAuth client's registration (`registered`), the
 * app's own manifest (`unvouched`), pointed to by `hdsApp.manifest` (`clientData.hdsApp` on a plain access; for a
 * CMC relationship, the invite's `requesterMeta.hdsApp`, which the person's app mirrors as `content.hdsApp` on her
 * accept event, read through `clientData.cmc.acceptEventId`). Pure but for `loadAppManifest` (one fetch per URL per
 * session, never throws), `manifestPointerOfAccess` (reads the accept event) and `mirrorAppPointerOnAcceptEvent`.
 * The words shown around a description ("not listed by Health Data Safe") are the apps'.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.manifestPointerOf = manifestPointerOf;
exports.parseAppManifest = parseAppManifest;
exports.loadAppManifest = loadAppManifest;
exports.clearAppManifests = clearAppManifests;
exports.vouchingEntry = vouchingEntry;
exports.needsManifest = needsManifest;
exports.describeApp = describeApp;
exports.appIdentityOfAccess = appIdentityOfAccess;
exports.manifestPointerOfAccess = manifestPointerOfAccess;
exports.mirrorAppPointerOnAcceptEvent = mirrorAppPointerOnAcceptEvent;
const MAX_URL = 2048;
const MAX_BYTES = 64 * 1024;
const TIMEOUT_MS = 5000;
function httpsUrl(value) {
    if (typeof value !== 'string' || value.length > MAX_URL)
        return null;
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password)
            return null;
        return url.href;
    }
    catch {
        return null;
    }
}
const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
function localized(raw, check = text) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const out = {};
    for (const [lang, v] of Object.entries(raw)) {
        const value = check(v);
        if (value)
            out[lang] = value;
    }
    return out.en ? out : null;
}
function openLinks(raw) {
    const out = {};
    if (typeof raw !== 'object' || raw === null)
        return out;
    for (const k of ['web', 'ios', 'android']) {
        const url = httpsUrl(raw[k]);
        if (url)
            out[k] = url;
    }
    return out;
}
/** The manifest URL in an `hdsApp` holder (an access's `clientData`, an invite's `requesterMeta`), or null */
function manifestPointerOf(holder) {
    const raw = holder?.hdsApp?.manifest;
    const url = httpsUrl(raw);
    return url && !new URL(url).hash ? url : null;
}
/** An untrusted manifest body as an `AppManifest`, or null; a bad optional field is dropped, never the manifest */
function parseAppManifest(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const m = raw;
    if (m.format !== 'hds-app-manifest' || m.formatVersion !== 1)
        return null;
    const appCode = text(m.appCode);
    const name = localized(m.name);
    if (!appCode || !name)
        return null;
    const out = { appCode, name, open: openLinks(m.open) };
    const description = localized(m.description);
    if (description)
        out.description = description;
    const icon = httpsUrl(m.icon);
    if (icon)
        out.icon = icon;
    const p = m.provider;
    const providerName = localized(p?.name);
    if (providerName) {
        out.provider = { name: providerName };
        const url = httpsUrl(p?.url);
        if (url)
            out.provider.url = url;
    }
    const privacy = localized(m.privacy, httpsUrl);
    if (privacy)
        out.privacy = privacy;
    const support = httpsUrl(m.support);
    if (support)
        out.support = support;
    return out;
}
const loads = new Map();
/**
 * The manifest at `url`, read once per session (a failure is retried on the next call); never throws. No credentials,
 * no referrer, no redirect, a JSON content type, 64 KB, 5 s.
 */
function loadAppManifest(url, fetchImpl = fetch) {
    let p = loads.get(url);
    if (!p) {
        p = fetchManifest(url, fetchImpl);
        loads.set(url, p);
        p.then(m => { if (m === null && loads.get(url) === p)
            loads.delete(url); });
    }
    return p;
}
async function fetchManifest(url, fetchImpl) {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
    try {
        const res = await fetchImpl(url, { credentials: 'omit', mode: 'cors', referrerPolicy: 'no-referrer', redirect: 'error', signal: abort.signal });
        if (!res.ok || !/json/i.test(res.headers.get('content-type') ?? ''))
            return null;
        if (Number(res.headers.get('content-length') ?? 0) > MAX_BYTES)
            return null;
        const body = await res.text();
        if (body.length > MAX_BYTES)
            return null;
        return parseAppManifest(JSON.parse(body));
    }
    catch {
        return null;
    }
    finally {
        clearTimeout(timer);
    }
}
/** Forget what was read (tests, sign-out) */
function clearAppManifests() {
    loads.clear();
}
/** The catalogue entry that vouches for this app: by `appCode` / `id`, and for an entry with `counterparty`, by it too */
function vouchingEntry(input) {
    return (input.catalogue ?? []).find(e => {
        if (e.appCode !== input.appCode && e.id !== input.appCode)
            return false;
        if (!e.counterparty)
            return true;
        const c = input.counterparty;
        return c?.host === e.counterparty.host && (e.counterparty.username == null || c?.username === e.counterparty.username);
    }) ?? null;
}
/** Whether `describeApp` would use a manifest: no entry supplies the words, and the app points at one */
function needsManifest(input) {
    if (!input.pointer)
        return false;
    const entry = vouchingEntry(input);
    if (!entry)
        return !input.oauthClient?.clientName;
    return sameUrl(entry.manifest, input.pointer) && !(entry.name && entry.description && entry.icon);
}
function sameUrl(a, b) {
    return a != null && b != null && httpsUrl(a) === httpsUrl(b);
}
const hostOf = (url) => new URL(url).host;
/** The one rule (APP-DESCRIPTION.md § The rule): first match wins */
function describeApp(input) {
    const { appCode, pointer, oauthClient } = input;
    const manifest = input.manifest && input.manifest.appCode === appCode ? input.manifest : null;
    const entry = vouchingEntry(input);
    if (entry) {
        const fill = manifest && pointer && sameUrl(entry.manifest, pointer) ? manifest : null;
        const name = entry.name ? { en: entry.name } : fill?.name ?? null;
        const description = localized(entry.description) ?? fill?.description;
        const icon = entry.icon ?? (fill?.icon ? { type: 'url', value: fill.icon } : undefined);
        const open = Object.keys(openLinks(entry.urls)).length > 0 ? openLinks(entry.urls) : fill?.open ?? {};
        const provider = entry.provider ?? fill?.provider?.name;
        return {
            trust: 'vouched',
            appCode,
            name,
            ...(description && { description }),
            ...(icon && { icon }),
            open,
            ...(provider && { provider }),
            ...(fill && pointer && { host: hostOf(pointer) })
        };
    }
    if (oauthClient?.clientName) {
        const logo = httpsUrl(oauthClient.logoUri);
        const web = httpsUrl(oauthClient.clientUri);
        return {
            trust: 'registered',
            appCode,
            name: { en: oauthClient.clientName },
            ...(logo && { icon: { type: 'url', value: logo } }),
            open: web ? { web } : {}
        };
    }
    if (manifest && pointer) {
        return {
            trust: 'unvouched',
            appCode,
            name: manifest.name,
            ...(manifest.description && { description: manifest.description }),
            open: manifest.open,
            ...(manifest.provider && { provider: manifest.provider.name }),
            host: hostOf(pointer)
        };
    }
    return { trust: 'none', appCode, name: null, open: {} };
}
/** Who an access says it is: its app code (`cmc.appCode`, else the access name = `requestingAppId`) and its CMC counterparty */
function appIdentityOfAccess(access) {
    const cmc = access.clientData?.cmc;
    const appCode = typeof cmc?.appCode === 'string' && cmc.appCode ? cmc.appCode : access.name ?? '';
    return { appCode, counterparty: cmc?.counterparty ?? null };
}
/**
 * The manifest URL an access points at: `clientData.hdsApp` on a plain access; for a CMC relationship, `content.hdsApp`
 * on the person's accept event (`clientData.cmc.acceptEventId`; the offer itself is gone behind the consumed
 * capability). Never throws.
 */
async function manifestPointerOfAccess(connection, access) {
    const own = manifestPointerOf(access.clientData);
    if (own)
        return own;
    const acceptEventId = access.clientData?.cmc?.acceptEventId;
    if (typeof acceptEventId !== 'string')
        return null;
    try {
        const accept = await connection.apiOne('events.getOne', { id: acceptEventId }, 'event');
        return manifestPointerOf(accept?.content);
    }
    catch {
        return null;
    }
}
/**
 * Person-side, at accept: mirror the invite's `requesterMeta.hdsApp` (read before consent, e.g. through
 * `cmcFormSpec.readOfferWithFormSpec(...).content.requesterMeta`) onto her `consent/accept-cmc` event as
 * `content.hdsApp`, as `mirrorFormSpecOnAcceptEvent` does for the data set. Writes nothing (and returns false) when
 * the invite carries no valid pointer.
 */
async function mirrorAppPointerOnAcceptEvent(connection, acceptEventId, requesterMeta) {
    const manifest = manifestPointerOf(requesterMeta);
    if (!manifest)
        return false;
    const current = await connection.apiOne('events.getOne', { id: acceptEventId }, 'event');
    await connection.apiOne('events.update', { id: acceptEventId, update: { content: { ...(current?.content ?? {}), hdsApp: { manifest } } } }, 'event');
    return true;
}
